import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useUpdateEffect } from 'usehooks-ts';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { TablePaginationConfig } from 'antd';
import Skeleton from 'react-loading-skeleton';

import './index.less';

import { alertsSelectorAdd, currentFolderIdSelector } from '../../../../recoil';
import { ProviderModel, ToolsetModel, ToolsetSettingsType, ToolsetState } from '../../../../../gamma-api';
import { getPageFromSearchParams, PAGE_INDEX_PARAM } from '../../../../utils/urlUtil';
import { defaultApi } from '../../../../apis';
import IbTypography from '../../../../components/common/IbTypography';
import IbButton from '../../../../components/common/IbButton';
import IbConfirmModal from '../../../../components/common/IbConfirmModal';
import { SourcesEmptyLogo } from '../../../../assets';
import IbIcon from '../../../../components/common/IbIcon';

import {
  EMPTY_CONTENT_CLASS_NAME,
  EMPTY_CONTENT_PLACEHOLDER_CLASS_NAME,
  getEmptyToolsetForType,
  MAIN_CLASS_NAME,
  SKELETON_BORDER_RADIUS,
  SKELETON_CONTAINER_CLASS_NAME,
  SKELETON_HEIGHT,
  TOOLSETS_PAGE_SIZE,
} from './const';
import ToolsetModal from './ToolsetModal';
import ToolsetList from './ToolsetList';
import { translate } from './utils';

export interface IToolsetsTabProps {
  agentId: string;
  providers: ProviderModel[];
  providersLoading: boolean;
}

const ToolsetsTab: React.FC<IToolsetsTabProps> = ({ agentId, providers, providersLoading }) => {
  const { push } = useHistory();

  const addAlert = useSetRecoilState(alertsSelectorAdd);
  const folderId = useRecoilValue(currentFolderIdSelector);

  const [itemLoading, setItemLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const [creationToolset, setCreationToolset] = useState<ToolsetModel>();
  const [editingToolset, setEditingToolset] = useState<ToolsetModel>();
  const [deletionToolset, setDeletionToolset] = useState<ToolsetModel>();

  const [toolsetList, setToolsetList] = useState<ToolsetModel[]>([]);

  const [toolsetPagination, setToolsetPagination] = useState<TablePaginationConfig>({
    current: getPageFromSearchParams(location.search),
    pageSize: TOOLSETS_PAGE_SIZE,
    total: 0,
  });

  const toolsetListIsEmpty = !listLoading && !toolsetList.length;

  const fetchToolsetList = async () => {
    try {
      const response = await defaultApi.searchToolsets(
        undefined,
        undefined,
        [agentId],
        (toolsetPagination.current || 1) - 1,
        TOOLSETS_PAGE_SIZE
      );
      setToolsetList(response.data.items || []);
      setToolsetPagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (err) {
      addAlert({ content: translate('Failed to load toolsets'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const onInitialLoadAsync = async () => {
    setListLoading(true);
    await fetchToolsetList();
    setListLoading(false);
  };

  const onInitialLoadSync = () => {
    onInitialLoadAsync().finally();
  };

  useEffect(onInitialLoadSync, []);

  const onToolsetPageOrSearchChange = async () => {
    setPageLoading(true);
    await fetchToolsetList();
    setPageLoading(false);
  };

  useEffect(() => {
    onToolsetPageOrSearchChange().finally();
  }, [toolsetPagination.current, toolsetPagination.pageSize, agentId]);

  useEffect(() => {
    const page = getPageFromSearchParams(location.search);
    if (page !== toolsetPagination.current) {
      setToolsetPagination((prev) => ({ ...prev, current: page }));
    }
  }, [location.search]);

  useUpdateEffect(() => {
    if (toolsetPagination.current) {
      const params = new URLSearchParams(location.search);
      params.set(PAGE_INDEX_PARAM, toolsetPagination.current.toString());
      push({ pathname: location.pathname, search: params.toString() });
    }
  }, [toolsetPagination.current]);

  const onCreationButtonClick = () => {
    setCreationToolset({ ...getEmptyToolsetForType(agentId) });
  };

  const onToolsetPaginationChange = (page: number) => setToolsetPagination({ ...toolsetPagination, current: page });

  const onToolsetModalClose = () => {
    setEditingToolset(undefined);
    setCreationToolset(undefined);
  };

  const onToolsetModalTypeChange = (type: ToolsetSettingsType) => {
    setCreationToolset({ ...getEmptyToolsetForType(agentId, type) });
  };

  const onToolsetModalSave = async (toolset?: ToolsetModel) => {
    if (!toolset) {
      return;
    }

    try {
      if (toolset.toolset_id) {
        await defaultApi.updateToolset(toolset.toolset_id, {
          agent_id: agentId,
          state: toolset.state,
          type: toolset.type,
          settings: toolset.settings,
        });
      } else {
        await defaultApi.createToolset({
          folder_id: folderId,
          agent_id: agentId,
          state: toolset.state,
          type: toolset.type,
          settings: toolset.settings,
        });
      }

      await fetchToolsetList();
      setEditingToolset(undefined);
      setCreationToolset(undefined);
    } catch (err) {
      addAlert({ content: translate('Failed to save toolset'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const onToolsetStateChange = async (toolset: ToolsetModel, state: ToolsetState) => {
    try {
      await defaultApi.updateToolset(toolset.toolset_id, {
        ...toolset,
        state,
      });
      await fetchToolsetList();
    } catch (err) {
      addAlert({ content: translate('Failed to save toolset'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const onToolsetDelete = (toolset: ToolsetModel) => {
    setDeletionToolset(toolset);
  };

  const onToolsetSetup = (toolset: ToolsetModel) => {
    setEditingToolset(toolset);
  };

  const renderToolsetModal = () => {
    return (
      <ToolsetModal
        providers={providers}
        providersLoading={providersLoading}
        toolset={creationToolset || editingToolset}
        visible={!!(creationToolset || editingToolset)}
        onClose={onToolsetModalClose}
        onSave={onToolsetModalSave}
        onTypeChange={onToolsetModalTypeChange}
      />
    );
  };

  const renderDeletionConfirmModal = () => {
    const onConfirmClick = async () => {
      if (!deletionToolset) {
        return;
      }

      setItemLoading(true);

      try {
        await defaultApi.deleteToolset(deletionToolset.toolset_id);
      } catch (err) {
        addAlert({ content: translate('Failed to delete toolset'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
        setItemLoading(false);
        return;
      }

      await fetchToolsetList();

      setItemLoading(false);
      setDeletionToolset(undefined);
    };

    const onCancelClick = () => {
      setDeletionToolset(undefined);
    };

    return (
      <IbConfirmModal
        footer={
          <>
            <IbButton key="delete-confirm" status="error" onClick={onConfirmClick}>
              {translate('Delete')}
            </IbButton>
            <IbButton key="delete-cancel" type="secondary" onClick={onCancelClick}>
              {translate('Cancel')}
            </IbButton>
          </>
        }
        loading={itemLoading}
        title={translate('Delete toolset?')}
        visible={!!deletionToolset}
        onCancel={onCancelClick}
      >
        <IbTypography.Paragraph type="secondary">
          {translate('Are you sure you want to remove the toolset and its content?')}
        </IbTypography.Paragraph>
      </IbConfirmModal>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      {toolsetListIsEmpty ? (
        <div className={EMPTY_CONTENT_CLASS_NAME}>
          <div className={EMPTY_CONTENT_PLACEHOLDER_CLASS_NAME}>
            <SourcesEmptyLogo />
            <IbTypography.Paragraph disabled type="secondary">
              {translate('Empty toolset list text: line 1')}
              <br />
              {translate('Empty toolset list text: line 2')}
            </IbTypography.Paragraph>
            <IbButton icon={<IbIcon iconName="add-one" />} onClick={onCreationButtonClick}>
              {translate('Tool')}
            </IbButton>
          </div>
        </div>
      ) : (
        <>
          {listLoading && (
            <div className={SKELETON_CONTAINER_CLASS_NAME}>
              <Skeleton borderRadius={SKELETON_BORDER_RADIUS} height={SKELETON_HEIGHT} />
            </div>
          )}
          {!listLoading && (
            <IbButton icon={<IbIcon iconName="add-one" />} type="primary" onClick={onCreationButtonClick}>
              {translate('Add')}
            </IbButton>
          )}
          <ToolsetList
            dataLoading={listLoading}
            pageLoading={pageLoading}
            pagination={toolsetPagination}
            toolsets={toolsetList}
            onDelete={onToolsetDelete}
            onPaginationChange={onToolsetPaginationChange}
            onSetup={onToolsetSetup}
            onToolsetStateChange={onToolsetStateChange}
          />
        </>
      )}
      {renderToolsetModal()}
      {renderDeletionConfirmModal()}
    </div>
  );
};

export default ToolsetsTab;
